import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar, Shield, CheckCircle, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';

const Results = () => {
  // PLACEHOLDER: Real project data should be added here
  const placeholderProjects = [
    {
      id: 1,
      year: 'PLACEHOLDER',
      make: 'Upload Real Photos',
      model: 'Black Porsche 911',
      color: 'Black',
      service: 'Paint Correction & Ceramic Coating',
      beforeImage: 'https://images.unsplash.com/photo-1708805282706-f44730b7e527?w=800&q=80',
      afterImage: 'https://images.unsplash.com/photo-1708805282676-0c15476eb8a2?w=800&q=80',
      startingCondition: 'Swirl marks, light scratches, and water spotting visible under direct sunlight',
      workCompleted: '2-stage paint correction, decontamination, ceramic coating application',
      result: 'Deep gloss restored with enhanced clarity and hydrophobic protection',
      location: 'Simi Valley'
    },
    {
      id: 2,
      year: 'PLACEHOLDER',
      make: 'Upload Real Photos',
      model: 'BMW M3',
      color: 'White',
      service: 'Full Interior & Exterior Detail',
      beforeImage: 'https://images.unsplash.com/photo-1708805282695-ef186db20192?w=800&q=80',
      afterImage: 'https://images.unsplash.com/photo-1708805282676-0c15476eb8a2?w=800&q=80',
      startingCondition: 'Heavy interior dust, exterior contaminants, swirls visible',
      workCompleted: 'Interior deep clean, paint decontamination, 1-stage correction, sealant',
      result: 'Refreshed interior and improved exterior clarity',
      location: 'Thousand Oaks'
    }
  ];

  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Car Detailing Before & After | Detailing Disciples"
        description="See real Detailing Disciples paint correction, ceramic coating, interior detailing and maintenance results on client vehicles."
        canonical="https://thedetailingdisciples.com/results"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              Detailing Results on <span className="text-[#B8860B]">Real Client Vehicles</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Browse actual paint correction, ceramic coating, interior detailing, and maintenance work completed by Detailing Disciples.
            </p>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 bg-[#B8860B]/10 border-y border-[#B8860B]/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#B8860B] font-semibold text-lg">
              ⚠️ PLACEHOLDER IMAGES - Real project photos needed from owner
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Stock photos below will be replaced with actual Detailing Disciples work
            </p>
          </div>
        </div>
      </section>

      {/* Results Grid */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-12">
            {placeholderProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-[#B8860B]/50 transition-all"
              >
                {/* Before/After Images */}
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative group">
                    <img
                      src={project.beforeImage}
                      alt={`${project.color} ${project.year} ${project.make} ${project.model} before detailing`}
                      className="w-full h-80 object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-lg font-bold">
                      BEFORE
                    </div>
                  </div>
                  <div className="relative group">
                    <img
                      src={project.afterImage}
                      alt={`${project.color} ${project.year} ${project.make} ${project.model} after ${project.service} by Detailing Disciples in ${project.location}`}
                      className="w-full h-80 object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-[#B8860B] text-[#111216] px-4 py-2 rounded-lg font-bold">
                      AFTER
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="p-8 space-y-4">
                  <h2 className="text-2xl font-bold text-white">
                    {project.year} {project.make} {project.model} — {project.service}
                  </h2>

                  <div className="grid md:grid-cols-3 gap-6 text-sm">
                    <div>
                      <p className="text-[#B8860B] font-semibold mb-2">Starting Condition:</p>
                      <p className="text-gray-300">{project.startingCondition}</p>
                    </div>
                    <div>
                      <p className="text-[#B8860B] font-semibold mb-2">Work Completed:</p>
                      <p className="text-gray-300">{project.workCompleted}</p>
                    </div>
                    <div>
                      <p className="text-[#B8860B] font-semibold mb-2">Result:</p>
                      <p className="text-gray-300">{project.result}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <p className="text-gray-400 text-sm">
                      <span className="text-[#B8860B] font-semibold">Location:</span> {project.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Image Requirements Note */}
          <div className="max-w-4xl mx-auto mt-16 bg-[#B8860B]/10 border border-[#B8860B]/30 rounded-2xl p-8">
            <h3 className="text-white font-bold text-xl mb-4 flex items-center">
              <Sparkles className="w-6 h-6 text-[#B8860B] mr-2" />
              Real Photos Needed
            </h3>
            <div className="space-y-2 text-gray-300 text-sm">
              <p className="font-semibold">For each project, please provide:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Before photo (showing swirls, defects, or starting condition)</li>
                <li>After photo (showing results)</li>
                <li>Vehicle year, make, model, color</li>
                <li>Service performed</li>
                <li>Starting condition description</li>
                <li>Work completed description</li>
                <li>Result description (realistic, not "perfect")</li>
                <li>City/location (if approved by client)</li>
              </ul>
              <p className="mt-4 text-[#B8860B]">
                Recommended dimensions: 1200x800px minimum, JPG or WebP format
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Want Results Like These?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Tell us about your vehicle and the results you want to achieve.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#B8860B] hover:bg-[#C8960B] text-[#111216] font-bold"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Request a Personalized Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Results;
